<template>
  <div
    class="flex items-center gap-6 p-4 bg-black/80 rounded-xl backdrop-blur-sm select-none touch-none shadow-2xl"
    @contextmenu.prevent
    @dragstart.prevent>
    <!-- Joystick (Pan/Tilt) -->
    <div
      ref="joystickRef"
      class="relative w-32 h-32 bg-gray-700/50 rounded-full border-2 border-gray-500 shadow-inner"
      @mousedown="startJoystick"
      @touchstart.prevent="startJoystick">
      <!-- Center Indicator -->
      <div class="absolute inset-0 m-auto w-2 h-2 bg-gray-500/30 rounded-full pointer-events-none"></div>

      <!-- Knob -->
      <div
        class="absolute w-12 h-12 bg-blue-500 rounded-full shadow-lg border-2 border-blue-400 cursor-grab active:cursor-grabbing hover:bg-blue-400 transition-colors pointer-events-none"
        :style="{
          left: '50%',
          top: '50%',
          transform: `translate(-50%, -50%) translate(${joystickUi.x}px, ${joystickUi.y}px)`,
        }"></div>
    </div>

    <!-- Zoom Slider -->
    <div class="relative h-32 w-10 flex justify-center">
      <!-- Track -->
      <div class="absolute h-full w-2 bg-gray-700/50 rounded-full border border-gray-600"></div>
      <!-- Center Tick -->
      <div class="absolute top-1/2 w-4 h-0.5 bg-gray-500 -translate-x-1/2 left-1/2"></div>

      <!-- Touch Area for slider -->
      <div
        ref="zoomRef"
        class="absolute inset-0 cursor-pointer -mx-4"
        @mousedown="startZoom"
        @touchstart.prevent="startZoom"></div>

      <!-- Thumb -->
      <div
        class="absolute left-1/2 w-8 h-8 bg-gray-600 rounded-md shadow-lg border-2 border-gray-400 cursor-grab active:cursor-grabbing pointer-events-none flex items-center justify-center transition-transform duration-75"
        :style="{ top: '50%', transform: `translate(-50%, -50%) translate(0, ${zoomUi}px)` }">
        <span class="text-white/80 font-bold text-xs">Z</span>
      </div>

      <!-- Icons -->
      <div class="absolute -top-6 text-gray-400 text-xs font-bold select-none">+</div>
      <div class="absolute -bottom-6 text-gray-400 text-xs font-bold select-none">-</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onUnmounted } from 'vue';

const emit = defineEmits<{
  (e: 'update', value: { x: number; y: number; zoom: number }): void;
}>();

const joystickRef = ref<HTMLElement | null>(null);
const zoomRef = ref<HTMLElement | null>(null);

const joystickState = reactive({
  active: false,
  x: 0,
  y: 0,
});

const joystickUi = reactive({
  x: 0,
  y: 0,
});

const zoomState = reactive({
  active: false,
  val: 0,
});

const zoomUi = ref(0);

const MAX_RADIUS = 40; // Max travel in pixels
const MAX_ZOOM_TRAVEL = 48; // Max travel up/down in pixels

// Global event handlers
const onMove = (e: MouseEvent | TouchEvent) => {
  if (joystickState.active) {
    handleJoystickMove(e);
  }
  if (zoomState.active) {
    handleZoomMove(e);
  }
};

const onEnd = () => {
  let changed = false;
  if (joystickState.active) {
    joystickState.active = false;
    joystickState.x = 0;
    joystickState.y = 0;
    joystickUi.x = 0;
    joystickUi.y = 0;
    changed = true;
  }
  if (zoomState.active) {
    zoomState.active = false;
    zoomState.val = 0;
    zoomUi.value = 0;
    changed = true;
  }

  if (changed) emitUpdate();
  removeListeners();
};

function addListeners() {
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onEnd);
  window.addEventListener('touchmove', onMove, { passive: false });
  window.addEventListener('touchend', onEnd);
  window.addEventListener('touchcancel', onEnd);
}

function removeListeners() {
  window.removeEventListener('mousemove', onMove);
  window.removeEventListener('mouseup', onEnd);
  window.removeEventListener('touchmove', onMove);
  window.removeEventListener('touchend', onEnd);
  window.removeEventListener('touchcancel', onEnd);
}

onUnmounted(() => {
  removeListeners();
});

function getClientCoords(e: MouseEvent | TouchEvent) {
  if ('touches' in e) {
    return { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }
  return { x: e.clientX, y: e.clientY };
}

// Joystick Logic
function startJoystick(e: MouseEvent | TouchEvent) {
  joystickState.active = true;
  handleJoystickMove(e);
  addListeners();
}

function handleJoystickMove(e: MouseEvent | TouchEvent) {
  if (!joystickRef.value) return;
  const rect = joystickRef.value.getBoundingClientRect();
  const center = {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
  const coords = getClientCoords(e);

  let dx = coords.x - center.x;
  let dy = coords.y - center.y;

  const distance = Math.sqrt(dx * dx + dy * dy);

  // Clamp to radius
  if (distance > MAX_RADIUS) {
    const angle = Math.atan2(dy, dx);
    dx = Math.cos(angle) * MAX_RADIUS;
    dy = Math.sin(angle) * MAX_RADIUS;
  }

  joystickUi.x = dx;
  joystickUi.y = dy;

  // Normalize -1 to 1
  // Invert Y because screen Y+ is Down, but Pan/Tilt Y+ is usually Up
  joystickState.x = dx / MAX_RADIUS;
  joystickState.y = -(dy / MAX_RADIUS);

  emitUpdate();
}

// Zoom Logic
function startZoom(e: MouseEvent | TouchEvent) {
  zoomState.active = true;
  handleZoomMove(e);
  addListeners();
}

function handleZoomMove(e: MouseEvent | TouchEvent) {
  if (!zoomRef.value) return;
  // Use the container's rect for consistent center relative calculation
  const rect = zoomRef.value.parentElement!.getBoundingClientRect();
  const centerY = rect.top + rect.height / 2;
  const coords = getClientCoords(e);

  let dy = coords.y - centerY;

  // Clamp
  if (dy > MAX_ZOOM_TRAVEL) dy = MAX_ZOOM_TRAVEL;
  if (dy < -MAX_ZOOM_TRAVEL) dy = -MAX_ZOOM_TRAVEL;

  zoomUi.value = dy;

  // Normalize -1 to 1
  // Screen Y+ is Down (Zoom Out), Screen Y- is Up (Zoom In)
  // But usually Zoom In is "Tele" and Zoom Out is "Wide"
  // Assuming slider up = Zoom In (Tele) = Positive
  zoomState.val = -(dy / MAX_ZOOM_TRAVEL);

  emitUpdate();
}

function emitUpdate() {
  emit('update', {
    x: Number(joystickState.x.toFixed(2)),
    y: Number(joystickState.y.toFixed(2)),
    zoom: Number(zoomState.val.toFixed(2)),
  });
}
</script>
